import { useRef, useEffect, useState } from 'react';

type TParams = {
  initialCounter: number;
  step?: number;
  startOnMount?: boolean;
  onCounterUpdate?: (x: number) => void;
};

type TReturn = {
  counter: number;
  isActive: boolean;
  reset(): void;
  toggle(): void;
  restart(): void;
};

const useTimer = (params: TParams): TReturn => {
  const { initialCounter = 0, step = 1000, startOnMount = true, onCounterUpdate } = params;

  const interval = useRef<number | null>(null);
  const [counter, setCounter] = useState(initialCounter);
  const [isActive, setIsActive] = useState(startOnMount);

  const toggle = () => {
    if (counter > 0) setIsActive(!isActive);
  };

  const reset = () => {
    setCounter(initialCounter);
    setIsActive(false);
  };

  const clear = () => {
    if (interval.current) window.clearInterval(interval.current);
  };

  const start = () => {
    interval.current = window.setInterval(() => {
      setCounter(prev => prev - 1);
      if (typeof onCounterUpdate === 'function') onCounterUpdate(counter - 1);
    }, step);
  };

  const restart = () => {
    clear();
    setCounter(initialCounter);
    setIsActive(true);
    start();
  };

  useEffect(() => {
    if (isActive) {
      start();
    } else {
      clear();
    }

    return () => {
      clear();
    };
  }, [isActive]);

  useEffect(() => {
    if (counter <= 0) setIsActive(false);
  }, [counter]);

  return { counter, isActive, reset, toggle, restart };
};

export default useTimer;
